import { useEffect, useState, useRef } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import apiInstance from "../../utils/axios";
import UserData from "../plugins/UserData";
import '../../../css/product.css';
import { Link, useNavigate } from "react-router-dom";
import { useAuthApp } from '../../app/auth';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import useAxios from '../../utils/useAxios'
import imageCompression from 'browser-image-compression';

const Toast = Swal.mixin({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

function ProductUpdate() {
    const [isLoading, setIsLoading] = useState(false);
    const [productNames, setProductNames] = useState([]);
    const [wineCategories, setWineCategories] = useState([])
    const [wineQualities, setWineQualities] = useState([])
    const [subCategories, setSubCategories] = useState([])
    const [blendings, setBlendings] = useState([])
    const [origins, setOrigins] = useState([])
    const [wineTypes, setWineTypes] = useState([])
    const [productId, setProductId] = useState([]);
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
    const [imagePreview, setImagePreview] = useState('')
    const [wineTypesInfo, setWineTypesInfo] = useState([])
    const [uploadProgress, setUploadProgress] = useState(0);

    const hasFetchedData = useRef(false);
    const {hashedId} = useParams()
    const { t } = useTranslation();

    let lang_browser = navigator.language.toLowerCase();
    let lang = localStorage.getItem('language') || (lang_browser.startsWith("en") ? "en" : lang_browser);

    const [formData, setFormData] = useState({
        product_name: '',
        category: '',
        sub_category: '',
        blending_type: '',
        wine_type: [],
        wine_qual: '',
        vintage: '',
        origin: '',
        product_picture: '',
    });

    const navigate = useNavigate()
    const api = useAxios();
    const userData = useAuthApp((state) => state.user)
    const userId = userData()?.user_id

    if (userId === undefined) {
        navigate('login/')    
    }

    const fetchProductNames = async () => {
        setIsLoading(true);
        try {
            const res = await apiInstance.get(`productnames/${userId}/`);
            const fetchedOptions = res.data.map((name) => ({
                value: name.id,
                label: name.name,
            }));
            setProductNames(fetchedOptions);
        } catch (error) {
            console.error("Error fetching product names:", error);
            Toast.fire({
                icon: "error",
                title: "Failed to fetch Product Names!",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchProductSelection = async () => {
        if (!api) return;
        setIsLoading(true)
        try {
            const res = await api.get(`product-selection/`);
            let fetchedWineCategories = res.data?.wine_categories.map((item) => ({
                value: item.id,
                label: item.translations[`${lang}`]?.name || item.translations['de']?.name
            }                
            ));

            fetchedWineCategories = [
                { value: "", label: "" },
                ...fetchedWineCategories, 
              ];

            setWineCategories(fetchedWineCategories);

            let fetchedWineQualities = res.data?.wine_qualities.map((item) => ({
                value: item.id,
                label: item.translations[`${lang}`]?.name || item.translations['de']?.name
            }                
            ));

            fetchedWineQualities = [
                { value: "", label: "" },
                ...fetchedWineQualities, 
              ];
            setWineQualities(fetchedWineQualities);

            let fetchedSubCategories = res.data?.sub_categories.map((item) => ({
                value: item.id,
                label: item.translations[`${lang}`]?.name || item.translations['de']?.name
            }                
            ));

            fetchedSubCategories = [
                { value: "", label: "" },
                ...fetchedSubCategories, 
              ];
            setSubCategories(fetchedSubCategories);

            let fetchedBlendings = res.data?.blendings.map((item) => ({
            value: item.id,
            label: item.translations[`${lang}`]?.name || item.translations['de']?.name
            }                
            ));

            fetchedBlendings = [
                { value: "", label: "" },
                ...fetchedBlendings,
              ];
            setBlendings(fetchedBlendings);

            let fetchedOrigins = res.data?.origins.map((item) => ({
            value: item.id,
            label: item.translations[`${lang}`]?.name || item.translations['de']?.name
            }                
            ));

            fetchedOrigins = [
                { value: "", label: "" },
                ...fetchedOrigins,
              ];
            setOrigins(fetchedOrigins);

            const fetchedWineTypes = res.data?.wine_types.map((item) => ({
                value: item.id,
                label: item.name
            }                
            ));
            
            setWineTypesInfo(res.data?.wine_types)
            setWineTypes(fetchedWineTypes);
            
        } catch (error) {
            
        } finally {
            setIsLoading(false);
        }
    }

    const getProductDetails = async (hashedId) => {
        try {
            const decodeResponse = await apiInstance.get(`decode-id/${hashedId}/`);
            setProductId(decodeResponse.data.id);
            const productResponse = await apiInstance.get(`products/${decodeResponse.data.id}/`);
            return productResponse.data; // Return the product details
        } catch (error) {
            console.error("Error fetching product details:", error);
            throw error; // Rethrow the error to be handled by the caller
        }
    };

    const product_param = async (hashedId) => {
        try {
            const productDetails = await getProductDetails(hashedId);            
            const selProductName = productNames.find(productName => productName.label === productDetails.product_name) || null;
            const selWineCat = wineCategories.find(
                (wineCat) => 
                    wineCat.value === productDetails.category_id // Match German
            ) || '';

            const selWineQual = wineQualities.find(
                (wineQual) => 
                    wineQual.value === productDetails.wine_qual_id // Match German
            ) || '';

            const selSubCat = subCategories.find(
                (subCat) => 
                    subCat.value === productDetails.sub_category_id // Match German
            ) || '';


            const selBlending = blendings.find(
                (blending) => 
                    blending.value === productDetails.blending_type_id // Match German
            ) || '';

            const selOrigin = origins.find(
                (origin) => 
                    origin.value === productDetails.origin_id // Match German
            ) || '';
    
            const selVintage = productDetails.vintage
            const selWineTypes = wineTypes.filter(type => {
                return productDetails.wine_type.includes(type.label);
            });

            const selWineTypeValues = selWineTypes.map(type => type.value);
            const productImage = productDetails.product_picture
            setImagePreview(productDetails.product_picture);

            return {
                selProductName,
                selWineCat,
                selWineQual,
                selSubCat,
                selBlending,
                selOrigin,
                selVintage,
                selWineTypeValues,
                productImage
            };
        } catch (error) {
            // Handle errors from getProductDetails
            console.error("Error in product_param:", error);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true); // Add loading state
            await Promise.all([fetchProductNames(), fetchProductSelection()]);
            if (hashedId && 
                productNames.length > 0 && 
                wineQualities.length > 0 && 
                wineCategories.length > 0 && 
                // subCategories.length > 0 && 
                // blendings.length > 0 && 
                origins.length > 0 && 
                wineTypes.length > 0) {
                    
                const data = await product_param(hashedId);

                if (data) {
                    setFormData(prev => ({
                        ...prev,
                        product_name: data.selProductName,
                        category: data.selWineCat,
                        wine_qual: data.selWineQual,
                        sub_category: data.selSubCat,
                        blending_type: data.selBlending,
                        origin: data.selOrigin,
                        vintage: data.selVintage,
                        wine_type: data.selWineTypeValues,
                    }));
                }
                hasFetchedData.current = true;
                setIsInitialDataLoaded(true);
            } else {
                setIsLoading(false); // Make sure to set loading to false even if condition fails
                setIsInitialDataLoaded(true); // Or consider setting it even if conditions fail, depending on desired behavior
            }
            
        };
        
        if (hashedId && !hasFetchedData.current) { // Check if fetch has already occurred
            fetchData();
            
        }
    }, [hashedId, isInitialDataLoaded, lang]);

    const handleChange = (eventOrSelectedOptions, { name, type }) => {
        setFormData((prev) => {
            let updatedValue;

            if (type === 'select-multiple') {
                updatedValue = eventOrSelectedOptions
                    ? eventOrSelectedOptions.map(option => option.value)
                    : []
                }
            else if (type === "select-one") {
                switch (name) {
                    case "product_name":
                        updatedValue = productNames.find((productName) => productName.value ===  (eventOrSelectedOptions ? eventOrSelectedOptions.value : null));
                        break;
                    case "category":
                        updatedValue = wineCategories.find((wineCategory) => wineCategory.value ===  (eventOrSelectedOptions ? eventOrSelectedOptions.value : null));

                        break
                    case "wine_qual":
                        updatedValue = wineQualities.find((wineQual) => wineQual.value ===  (eventOrSelectedOptions ? eventOrSelectedOptions.value : null));
                        break;
                    case "origin":
                        updatedValue = origins.find((origin) => origin.value ===  (eventOrSelectedOptions ? eventOrSelectedOptions.value : null));
                        break;
                    case "blending_type":
                        updatedValue = blendings.find((blending) => blending.value ===  (eventOrSelectedOptions ? eventOrSelectedOptions.value : null));
                        break;
                    case "sub_category":
                        updatedValue = subCategories.find((subCategory) => subCategory.value ===  (eventOrSelectedOptions ? eventOrSelectedOptions.value : null));
                        break;
                  }
            }
            else if (type === "file") {
                updatedValue = eventOrSelectedOptions ? eventOrSelectedOptions.target.files[0] : null;
            }
            else {
                updatedValue =  eventOrSelectedOptions ? eventOrSelectedOptions.target.value : null;
            }
            return {
                ...prev,
                [name]: updatedValue,
            };
        });
    };

    const validateForm = async (formData) => {
        if (formData.product_picture) {
            const fileSize = formData.product_picture.size / 1024 / 1024; // Convert size to MB
            if (fileSize > 10) {
                Toast.fire({
                    icon: "error",
                    title: t("product.form.messages.productPictureSizeWarning"),
                });
                return false;
            }
        }

        if (!formData.product_name ||
            !formData.category ||
            !formData.wine_type ||
            !formData.wine_qual ||
            !formData.origin
        ) {
            Toast.fire({
                icon: "error",
                title: t("product.form.messages.requiredFields"),
            })
            return false;
        }

        const selectedCategoryValue = formData.category?.value;
        const selectedCategoryLabel = formData.category?.label;

        // Determine the label for "Red wine" based on language
        const redWineLabel = lang === "de" ? "Rotwein" : "Red wine";

        // If the selected label is "Rosé", find the value for "Red wine"/"Rotwein"
        let adjustedCategoryValue = selectedCategoryValue;
        if (selectedCategoryLabel && selectedCategoryLabel.toLowerCase() === "rosé") {
            const redWineCategory = wineCategories.find(cat => cat.label === redWineLabel);
            if (redWineCategory) {
                adjustedCategoryValue = redWineCategory.value;
            }
        }
        // Get all selected wine type objects
        const selectedWineTypeObjects = wineTypesInfo.filter(item => formData.wine_type.includes(item.id));
        // Check if every selected wine type has the same category as the selected category
        const wineCategoryCheck = selectedWineTypeObjects.every(item => item.wine_category == adjustedCategoryValue);
        // --- Blend check ---
        const isBlendSelected = formData.blending_type && formData.blending_type.label && formData.blending_type.label.toLowerCase().includes(formData.blending_type.label.toLowerCase());
        if (isBlendSelected && formData.wine_type.length < 2) {
            await Swal.fire({
                icon: "warning",
                title: t("product.form.messages.blendingWineTypeWarning"),
                confirmButtonColor: "#3085d6",
            });
            return false;
        }

        if (!isBlendSelected && formData.wine_type.length > 1) {
                    await Swal.fire({
                        icon: "warning",
                        title: t("product.form.messages.blendingTypeWarning"),
                        confirmButtonColor: "#3085d6",
                    });
                    return false;
                }
        
        if (!wineCategoryCheck) {
            await Swal.fire({
                icon: "warning",
                title: t("product.form.messages.categoryCheckWarning"),
                confirmButtonColor: "#3085d6",
            });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        if (!api) return;
        e.preventDefault();

        const isValid = await validateForm(formData);
        if (!isValid) {
            return
        }

        const formDataToSend = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            if (value instanceof File) {
                // Handle File objects (like product_picture) directly
                formDataToSend.append(key, value);
            } else if (value instanceof Object) { // Check for Objects (non-File objects)
                if (Array.isArray(value)) {
                    // Handle multi-select fields (arrays of values)
                    value.forEach((val) => formDataToSend.append(key, val));
                } else if (value !== null && value !== undefined && value.hasOwnProperty('value')) {
                    // Check for objects with a 'value' property (e.g., category, origin, etc.)
                    formDataToSend.append(key, value.value);
                }
            } else {
                // Handle primitive values (strings, numbers, booleans) directly
                formDataToSend.append(key, value);
            }
        });
        
        try {
            setIsLoading(true);
            const response = await api.patch(`products/${productId}/`,  formDataToSend, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percent);
                },
                timeout: 35000 // 35 seconds timeout for this specific request
            })
    
            if (response.status === 200) {
                setIsLoading(false);
                const data = await response.data;
                const productId = data.id;

                Toast.fire({
                    icon: "success",
                    title: "Form submitted successfully!",
                })

                const resp = await apiInstance.get(`/hash-id/${productId}/`)
                const hashedId = resp.data.hashed_id
                navigate(`/product-nutrients/${hashedId}`)

            } else {
                setIsLoading(false);
                console.error("Error submitting form");
                Toast.fire({
                    icon: "error",
                    title: "Error in submitting form",
                })
            }
        } catch (error) {
            setIsLoading(false);
            if (error.code === 'ECONNABORTED') {
                alert("Upload timed out. Please check your internet connection and try again.");
            } else {
                alert("Upload failed. Please try again.");
            }
            Toast.fire({
                icon: "error",
                title: "Error in submitting form",
            })
        }
    };

    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Set compression options
        const options = {
            maxSizeMB: 1, // Target max size in MB
            maxWidthOrHeight: 1920, // Max width or height
            useWebWorker: true,
        };

        try {
            const compressedBlob = await imageCompression(file, options);
            const compressedFile = new File(
                [compressedBlob],
                file.name,
                { type: compressedBlob.type }
            );
            setImagePreview(URL.createObjectURL(compressedFile));
            setFormData((prev) => ({
                ...prev,
                product_picture: compressedFile,
            }));
        } catch (error) {
            console.error("Image compression error:", error);
        }
    };
    const customStyles = {
        control: (styles, { isFocused }) => ({
          ...styles,
          border: 'none', // Remove the border
          boxShadow: 'none', // Remove the blue outline on focus
          ':hover': {
              borderBottomColor: 'gray'
          }
        }),
          option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
          ...styles,
          fontSize: '16px',
          cursor: isDisabled ? 'not-allowed' : 'default',
          backgroundColor: isSelected ? '#eee' : isFocused ? '#f5f5f5' : 'white',
          color: isDisabled ? '#ccc' : 'black',
          height: '2.5rem',
          display: 'flex',
          alignItems: 'center',
        }),
        menu: (styles) => ({
          ...styles,
          width: 'calc(80% - 22px)',
          boxSizing: 'border-box',
        }),
          multiValue: (styles) => ({
            ...styles,
            backgroundColor: '#f0f0f0', // Example background color
            borderRadius: 4,
            margin: 2,
            padding: 3,
        }),
        multiValueRemove: (styles, { data }) => ({
            ...styles,
            color: 'gray',
            ':hover': {
                backgroundColor: 'lightgray',
                color: 'black',
                cursor: 'pointer',
            },
        }),
        indicatorSeparator: (styles) => ({ // Target the vertical line
            ...styles,
            display: 'none', // Hide the vertical line
          }),
        dropdownIndicator: (styles) => ({ // Target the dropdown arrow
        ...styles,
        padding: 3, 
        color: 'black', 
        svg: {
            width: '0.85em', // Adjust width to change arrow size
            height: '1em', // Adjust height to change arrow size
        },
        }),
      };

  return (
    <>
      <div className="container">
        <p className="form-heading-1">{t("product.form.headingUpdate")}</p>
        <form className="form-width-1" onSubmit={handleSubmit}>
        {formData.product_name && (
            <div className="form-input-container-1">
                <label htmlFor="productName" className="form-label-1 no-margin">{t("product.form.labels.productName")}</label>
                <Select
                    // isMulti
                    options={productNames}
                    value={formData.product_name}
                    onChange={(e) => handleChange(e, { name: 'product_name', type: "select-one"})}
                    className="form-input-2"
                    placeholder=""
                    isSearchable  // Enable search
                    styles={customStyles}
                />
            </div>
        )}
            <div className="form-input-container-1">
                <label htmlFor="wine_type" className="form-label-1 no-margin">{t("product.form.labels.wineType")}</label>
                <Select
                    isMulti
                    options={wineTypes}
                    value={formData.wine_type.map(value => wineTypes.find(option => option.value === value))}
                    onChange={(selectedOptions) => handleChange(selectedOptions, { name: 'wine_type' , type: 'select-multiple'})}
                    className="form-input-2"
                    placeholder=""
                    isSearchable  // Enable search
                    styles={customStyles}
                />
            </div>
            <div className="form-input-container-1">
                <label htmlFor="category" className="form-label-1 no-margin">{t("product.form.labels.category")}</label>
                <Select
                    // isMulti
                    options={wineCategories}
                    value={formData.category}
                    onChange={(e) => handleChange(e, { name: 'category' , type: "select-one"})}
                    className="form-input-2"
                    placeholder=""
                    isSearchable  // Enable search
                    styles={customStyles}
                />
            </div>
            <div className="form-input-container-1">
                <label htmlFor="wine_qual" className="form-label-1 no-margin">{t("product.form.labels.wineQuality")}</label>
                <Select
                    // isMulti
                    options={wineQualities}
                    value={formData.wine_qual}
                    onChange={(e) => handleChange(e, { name: 'wine_qual', type: "select-one" })}
                    className="form-input-2"
                    placeholder=""
                    isSearchable  // Enable search
                    styles={customStyles}
                />
            </div>
            <div className="form-input-container-1">
                <label htmlFor="origin" className="form-label-1 no-margin">{t("product.form.labels.origin")}</label>
                <Select
                    // isMulti
                    options={origins}
                    value={formData.origin}
                    onChange={(e) => handleChange(e, { name: 'origin', type: "select-one" })}
                    className="form-input-2"
                    placeholder=""
                    isSearchable  // Enable search
                    styles={customStyles}
                />
            </div>
            <div className="form-input-container-1">
                <label htmlFor="vintage" className="form-label-1 no-margin">{t("product.form.labels.vintage")}</label>
                <input
                    id="vintage"
                    name="vintage"
                    className="form-input-2"
                    value={formData.vintage}
                    onChange={(e) => handleChange(e, { name: 'vintage' })}
                    placeholder=""
                    type="number"
                />
            </div>
            <div className="form-input-container-1">
                <label htmlFor="blending_type" className="form-label-1 no-margin">{t("product.form.labels.blendingType")}</label>
                <Select
                    // isMulti
                    options={blendings}
                    value={formData.blending_type}
                    onChange={(e) => handleChange(e, { name: 'blending_type' , type: "select-one"})}
                    className="form-input-2"
                    placeholder=""
                    isSearchable  // Enable search
                    styles={customStyles}
                />
            </div>
            <div className="form-input-container-1">
                <label htmlFor="sub_category" className="form-label-1 no-margin">{t("product.form.labels.subCategory")}</label>
                <Select
                    // isMulti
                    options={subCategories}
                    value={formData.sub_category}
                    onChange={(e) => handleChange(e, { name: 'sub_category', type: "select-one" })}
                    className="form-input-2"
                    placeholder=""
                    isSearchable
                    styles={customStyles}
                />
            </div>
            <div className="form-input-container-1 form-adapt-button-left-1"> 
                <label htmlFor="productPicture" className="file-label-2">
                    <input
                        id="productPicture"
                        name="productPicture"
                        type="file"
                        className="file-input"
                        onChange={(e) => handleImageChange(e)}
                        
                    />
                    <span className="file-label-2-text">{imagePreview ? formData.product_name.label : t("product.form.labels.productPicture")}</span>
                </label>
                {imagePreview && (
                    <img src={imagePreview} alt="Wine bottle preview" className="image-preview" />
                )}
            </div>
            
            <div className="form-input-container-1">
                {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress-bar-container">
                    <div
                    className="upload-progress-bar"
                    style={{ width: `${uploadProgress}%`, height: "8px", background: "#4caf50" }}
                    />
                    <span>{uploadProgress}%</span>
                </div>
                )}
            </div>
            <div className="form-input-container-3">
            {isLoading 
            ? (<>
                <button disabled className="form-button-submit-2" type="submit">
                <i className="fas fa-spinner fa-spin"></i>
                <span className="mr-2">Processing</span>
                </button>
                </>)
            : <button className="form-button-submit-2" type="submit"><span className="mr-2">Submit</span>
                </button>
            }
            </div>
        </form>
      </div>      
    </>
  )
}

export default ProductUpdate